import { Chip, IconButton, Menu, MenuItem, Stack, TableCell, TableRow, Typography } from '@mui/material';
import moment from 'moment';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Iconify from '@src/components/iconify';
import SubscriptionDetailsModal from './subscription-details-modal';
import DeactivateModal from './deactivate-modal';
import TransactionHistoryModal from './transaction-history-modal';
import { useAuthService } from '@src/store/hooks';
import { cancelSubscriptionAPI, SubscriptionItem } from '@src/api/subscription';

const PLAN_COLORS: Record<string, { bgcolor: string; color: string }> = {
  DAY: { bgcolor: '#FFF3E0', color: '#E65100' },
  'Weekly VIP': { bgcolor: '#EDE8FF', color: '#6A00AC' },
  'Monthly VIP': { bgcolor: '#E6F9F0', color: '#00875A' },
};

const STATUS_COLORS: Record<string, { color: string }> = {
  ACTIVE: { color: '#00875A' },
  CANCELED: { color: '#DE350B' },
};

const TableRowItem = (props: SubscriptionItem) => {
  const navigate = useNavigate();
  const { currentUser } = useAuthService();

  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [deactivateOpen, setDeactivateOpen] = useState(false);
  const [transactionsOpen, setTransactionsOpen] = useState(false);

  const planLabel = props?.subscription?.name === 'Weekly VIP' ? 'Weekly' : props?.subscription?.name === 'DAY' ? 'Daily' : 'Monthly';
  const planStyle = PLAN_COLORS[props?.subscription?.name] || { bgcolor: '#F3F4F6', color: '#6B7280' };
  const statusStyle = STATUS_COLORS[props.status] || { color: '#6B7280' };

  const onViewUser = () => navigate(`/dashboard/users?u=${props.user?.id}`);

  const handleOpenDetails = () => {
    setAnchor(null);
    setDetailsOpen(true);
  };

  const handleOpenDeactivate = () => {
    setDetailsOpen(false);
    setDeactivateOpen(true);
  };

  const handleConfirmDeactivate = async (_params: { notes: string }) => {
    await cancelSubscriptionAPI(props.id);
  };

  const deactivatedBy = currentUser
    ? `${currentUser.displayName} (${currentUser.userTypeID === 3 ? 'Owner' : 'Admin'})`
    : 'Admin';

  return (
    <>
      <TableRow hover tabIndex={-1} role="checkbox">
        <TableCell align="left">
          <Typography
            variant="subtitle2"
            sx={{ textDecoration: 'underline', color: '#2D60FF', cursor: 'pointer' }}
            onClick={onViewUser}
          >
            {props.user?.id}
          </Typography>
        </TableCell>

        <TableCell align="left">
          <Typography variant="body2">{props.user?.displayName ?? '-'}</Typography>
        </TableCell>

        <TableCell align="left">
          <Chip
            label={planLabel}
            size="small"
            sx={{ bgcolor: planStyle.bgcolor, color: planStyle.color, fontWeight: 600, fontSize: 12, borderRadius: '6px' }}
          />
        </TableCell>

        <TableCell align="left">
          <Typography variant="body2">{'€'}{props.subscription.price}</Typography>
        </TableCell>

        <TableCell align="left">
          <Typography variant="body2" color={statusStyle.color}>
            {props.status}
          </Typography>
        </TableCell>

        <TableCell align="left">
          <Typography variant="body2">
            {props.startedAt ? moment.parseZone(props.startedAt).format('MM-DD-YYYY hh:mm A') : '-'}
          </Typography>
        </TableCell>

        <TableCell align="left">
          <Typography variant="body2">
            {props.expiresAt ? moment.parseZone(props.expiresAt).format('MM-DD-YYYY') : '-'}
          </Typography>
        </TableCell>

        <TableCell align="center">
          <IconButton onClick={(e) => setAnchor(e.currentTarget)} size="small">
            <Iconify icon="mdi:dots-vertical" width={20} />
          </IconButton>
          <Menu
            anchorEl={anchor}
            open={Boolean(anchor)}
            onClose={() => setAnchor(null)}
            PaperProps={{ sx: { borderRadius: '10px', minWidth: 170 } }}
          >
            <MenuItem onClick={() => { setAnchor(null); setTransactionsOpen(true); }} sx={{ fontSize: 13 }}>
              <Stack direction="row" alignItems="center" gap={1}>
                <Iconify icon="mdi:history" width={16} />
                View Transactions
              </Stack>
            </MenuItem>
            <MenuItem
              onClick={() => { setAnchor(null); handleOpenDetails(); }}
              sx={{ fontSize: 13, color: props.status === 'ACTIVE' ? '#DE350B' : '#9CA3AF' }}
              disabled={props.status !== 'ACTIVE'}
            >
              <Stack direction="row" alignItems="center" gap={1}>
                <Iconify icon="mdi:cancel" width={16} />
                Cancel Subscription
              </Stack>
            </MenuItem>
          </Menu>
        </TableCell>
      </TableRow>

      <SubscriptionDetailsModal
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        item={props}
        onDeactivate={handleOpenDeactivate}
      />

      <DeactivateModal
        open={deactivateOpen}
        onClose={() => setDeactivateOpen(false)}
        item={props}
        onConfirmDeactivate={handleConfirmDeactivate}
        deactivatedBy={deactivatedBy}
      />

      <TransactionHistoryModal
        open={transactionsOpen}
        onClose={() => setTransactionsOpen(false)}
        item={props}
      />
    </>
  );
};

export default TableRowItem;
